import * as Phaser from "phaser"
import { GameState } from "../core/game-state"
import { getHeroColorTheme } from "../core/hero-colors"
import { InputManager } from "../core/input-mapper"
import { SCENE } from "../core/keys"
import { PersistedData } from "../core/persisted-data"
import { THEME } from "../core/theme"
import { playClick, playHover } from "../core/ui-sfx"
import { CHARACTERS } from "../data/characters"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import type { Character } from "../types"
import { NeonPanel, RetroButton } from "../ui/components"
import * as NeonFX from "../ui/neon-fx"

const {
  drawCornerAccents,
  drawNeonBorder,
  drawNeonGrid,
  drawScanlines,
  neonTextShadow,
} = NeonFX

const FONT = THEME.fonts
const COLOR = THEME.colors
const ROLE_COLORS: Record<string, number> = {
  balanced: THEME.roles.balanced,
  tank: THEME.roles.tank,
  speed: THEME.roles.speed,
  multi: THEME.roles.multi,
  sniper: THEME.roles.sniper,
}

// Layout Constants for 960x540
const STAT_W = 820
const STAT_H = 120
const STAT_Y = GAME_HEIGHT - STAT_H - 85

const CARD_W = 160
const CARD_H = 200
const CARD_Y = 130
const CARD_SPACING = 40

export class CharacterSelectScene extends Phaser.Scene {
  private selectedIndex = 0
  private isButtonMode = false // false = cards, true = bottom buttons
  private selectedButtonIndex = 0 // 0 = deploy, 1 = back
  private isTransitioning = false
  private inputManager!: InputManager

  private cards: Phaser.GameObjects.Container[] = []
  private selectionCursor!: Phaser.GameObjects.Graphics
  private statPanel!: Phaser.GameObjects.Container
  private titleText!: Phaser.GameObjects.Text
  private deployBtn!: RetroButton
  private backBtn!: RetroButton

  constructor() {
    super({ key: SCENE.CHARACTER_SELECT })
  }

  private get selected(): Character {
    return CHARACTERS[this.selectedIndex]
  }

  create(): void {
    this.selectedIndex = 0
    this.isButtonMode = false
    this.selectedButtonIndex = 0
    this.isTransitioning = false

    this.inputManager = new InputManager(this, {
      onDown: (action, event) => {
        if (action === "LEFT") {
          this.navigate(-1)
          return
        }

        if (action === "RIGHT") {
          this.navigate(1)
          return
        }

        if (action === "UP") {
          if (this.isButtonMode) {
            this.isButtonMode = false
            this.updateSelection(true)
          }
        }

        if (action === "DOWN") {
          if (!this.isButtonMode) {
            this.isButtonMode = true
            this.updateSelection(true)
          }
        }

        if (action === "CONFIRM" || action === "FLAP") {
          // Ignore global pointer events for these actions in the menu
          if (event?.pointer) return

          if (!this.isButtonMode) {
            this.deploySelected()
          } else {
            if (this.selectedButtonIndex === 0) {
              this.deploySelected()
            } else {
              this.goBack()
            }
          }
          return
        }

        if (action === "BACK") {
          this.goBack()
        }
      },
    })

    this.buildBackground()
    this.buildCharacterList()
    this.buildStatPanel()
    this.buildChrome()

    this.updateSelection(false)

    this.cameras.main.fadeIn(400, 0, 0, 0)
    this.events.once("shutdown", this.onShutdown, this)
  }

  private onShutdown(): void {
    this.inputManager.destroy()
    this.tweens.killAll()
  }

  private buildBackground(): void {
    const bg = this.add.graphics()
    bg.fillGradientStyle(0x040612, 0x091224, 0x02030a, 0x08101f, 1, 1, 1, 1)
    bg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)

    const heroColors = getHeroColorTheme("bella")
    drawNeonGrid(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, heroColors.neon, 0.04, 60)
    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.015, 3).setDepth(200)
  }

  private buildCharacterList(): void {
    this.cards = []

    const totalW =
      CHARACTERS.length * CARD_W + (CHARACTERS.length - 1) * CARD_SPACING
    const startX = (GAME_WIDTH - totalW) / 2

    // Selection Cursor (Behind cards)
    this.selectionCursor = this.add.graphics()
    this.selectionCursor.setDepth(5)

    for (const [index, hero] of CHARACTERS.entries()) {
      const x = startX + index * (CARD_W + CARD_SPACING)
      const color = ROLE_COLORS[hero.role]

      const container = this.add.container(x, CARD_Y)
      container.setDepth(10)

      // Card Background
      const panel = new NeonPanel(this, {
        x: 0,
        y: 0,
        width: CARD_W,
        height: CARD_H,
        color: color,
        radius: 8,
        alpha: 0.2,
        showInner: true,
        showAccents: true,
        accentSize: 6,
      })
      container.add(panel)

      // Portrait
      const portrait = this.add.image(
        CARD_W / 2,
        CARD_H / 2 - 10,
        `portrait_${hero.id}`,
      )
      portrait.setScale(0.12)
      portrait.setAlpha(0.7)
      container.add(portrait)
      container.setData("portrait", portrait)

      // Name
      const nameText = this.add
        .text(CARD_W / 2, CARD_H - 30, hero.name.toUpperCase(), {
          fontSize: "18px",
          fontFamily: FONT.sans,
          fontStyle: "900",
          color: COLOR.whiteText,
        })
        .setOrigin(0.5)
      container.add(nameText)

      // Interaction Zone - Sized exactly to the card
      const zone = this.add
        .zone(CARD_W / 2, CARD_H / 2, CARD_W, CARD_H)
        .setInteractive({ useHandCursor: true })
      container.add(zone)

      this.inputManager.bindPointer(zone, {
        pointerover: () => {
          if (this.selectedIndex !== index && !this.isTransitioning) {
            this.selectedIndex = index
            this.updateSelection(true)
            playHover(this)
          }
        },
        pointerdown: () => {
          if (!this.isTransitioning) {
            if (this.selectedIndex !== index) {
              this.selectedIndex = index
              this.updateSelection(true)
              playHover(this)
            } else {
              this.deploySelected()
            }
          }
        },
      })

      this.cards.push(container)
    }
  }

  private buildStatPanel(): void {
    const x = (GAME_WIDTH - STAT_W) / 2
    this.statPanel = this.add.container(x, STAT_Y)
    this.statPanel.setDepth(100)

    const bg = new NeonPanel(this, {
      x: 0,
      y: 0,
      width: STAT_W,
      height: STAT_H,
      color: 0x0a0f1d,
      radius: 12,
      alpha: 0.85,
      showInner: true,
      showAccents: true,
    })
    this.statPanel.add(bg)
  }

  private updateSelection(animated: boolean): void {
    const hero = this.selected
    const color = ROLE_COLORS[hero.role]
    const selectedCard = this.cards[this.selectedIndex]

    // Update Cursor Position
    if (!this.isButtonMode) {
      this.selectionCursor.setVisible(true)
      if (animated) {
        this.tweens.add({
          targets: this.selectionCursor,
          x: selectedCard.x,
          y: selectedCard.y,
          duration: 200,
          ease: "Cubic.easeOut",
          onUpdate: () => {
            this.drawCursor(color)
          },
        })
      } else {
        this.selectionCursor.x = selectedCard.x
        this.selectionCursor.y = selectedCard.y
        this.drawCursor(color)
      }
    } else {
      this.selectionCursor.setVisible(false)
    }

    // Update Cards Look
    for (const [index, container] of this.cards.entries()) {
      const isSelected = !this.isButtonMode && index === this.selectedIndex
      const portrait = container.getData("portrait") as Phaser.GameObjects.Image
      if (!portrait) continue
      const targetAlpha = isSelected ? 1 : 0.5
      const targetScale = isSelected ? 0.14 : 0.12

      if (animated) {
        this.tweens.add({
          targets: portrait,
          alpha: targetAlpha,
          scale: targetScale,
          duration: 250,
          ease: "Back.easeOut",
        })
      } else {
        portrait.setAlpha(targetAlpha)
        portrait.setScale(targetScale)
      }
    }

    // Buttons look
    this.deployBtn.setSelected(
      this.isButtonMode && this.selectedButtonIndex === 0,
    )
    this.backBtn.setSelected(
      this.isButtonMode && this.selectedButtonIndex === 1,
    )

    this.updateButtonColors()
    this.rebuildStats()
  }

  private updateButtonColors(): void {
    const heroColors = getHeroColorTheme(this.selected.id)
    this.deployBtn.updateColor(heroColors.glow)
    this.backBtn.updateColor(heroColors.neon)
  }

  private drawCursor(_color: number): void {
    this.selectionCursor.clear()
    const heroColors = getHeroColorTheme(this.selected.id)
    drawNeonBorder(
      this.selectionCursor,
      -4,
      -4,
      CARD_W + 8,
      CARD_H + 8,
      heroColors.neon,
      12,
      0.8,
      0.2,
    )

    drawCornerAccents(
      this.selectionCursor,
      -8,
      -8,
      CARD_W + 16,
      CARD_H + 16,
      heroColors.neon,
      20,
      1,
    )
  }

  private rebuildStats(): void {
    const hero = this.selected
    const heroColors = getHeroColorTheme(hero.id)

    const toRemove = this.statPanel.list.slice(1)
    for (const obj of toRemove) {
      obj.destroy()
    }

    const sectionX = 30
    const col2 = 300
    const col3 = 560

    const nameLabel = this.add.text(sectionX, -35, hero.name.toUpperCase(), {
      fontSize: "32px",
      fontFamily: FONT.sans,
      fontStyle: "900",
      color: COLOR.whiteText,
      shadow: neonTextShadow(heroColors.glowText, 12),
    })
    this.statPanel.add([nameLabel])

    const breed = this.add.text(sectionX, 20, hero.breed.toUpperCase(), {
      fontSize: "11px",
      fontFamily: FONT.sans,
      fontStyle: "800",
      color: heroColors.glowText,
      letterSpacing: 2,
    })
    const desc = this.add.text(sectionX, 42, hero.description, {
      fontSize: "13px",
      fontFamily: FONT.sans,
      color: COLOR.whiteTextSoft,
      wordWrap: { width: 240 },
      lineSpacing: 4,
    })
    this.statPanel.add([breed, desc])

    const metrics = [
      { label: "HP", val: hero.maxHp },
      { label: "FLAP", val: `${hero.flapPower.toFixed(2)}x` },
      { label: "PERK", val: hero.perk },
    ]

    for (const [i, m] of metrics.entries()) {
      const label = this.add.text(col2, 20 + i * 28, m.label, {
        fontSize: "10px",
        fontFamily: FONT.sans,
        fontStyle: "900",
        color: heroColors.glowText,
      })
      const val = this.add.text(col2 + 55, 20 + i * 28, String(m.val), {
        fontSize: "11px",
        fontFamily: FONT.sans,
        fontStyle: "700",
        color: COLOR.whiteText,
        wordWrap: { width: 220 },
      })
      this.statPanel.add([label, val])
    }

    const loadoutLabel = this.add.text(col3, 20, "", {
      fontSize: "10px",
      fontFamily: FONT.sans,
      fontStyle: "900",
      color: heroColors.glowText,
    })
    const nextHint = this.add.text(col3, 38, "Who let the dogs out?", {
      fontSize: "13px",
      fontFamily: FONT.sans,
      fontStyle: "700",
      color: COLOR.whiteTextSoft,
      wordWrap: { width: 220 },
    })
    this.statPanel.add([loadoutLabel, nextHint])
  }

  private buildChrome(): void {
    const heroColors = getHeroColorTheme(this.selected.id)

    this.titleText = this.add
      .text(GAME_WIDTH / 2, 45, "SELECT YOUR PILOT", {
        fontSize: "36px",
        fontFamily: FONT.sans,
        fontStyle: "900",
        color: COLOR.whiteText,
        letterSpacing: 6,
        shadow: neonTextShadow(heroColors.glowShadow, 15),
      })
      .setOrigin(0.5)
      .setDepth(100)

    this.deployBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: GAME_WIDTH / 2 - 110,
      y: GAME_HEIGHT - 65,
      width: 220,
      height: 48,
      text: "LET'S BARK",
      color: heroColors.glow,
      isFilled: true,
      fontSize: "8px",
      onClick: () => this.deploySelected(),
    })
    this.deployBtn.setDepth(110)

    this.backBtn = new RetroButton(this, {
      inputManager: this.inputManager,
      x: 35,
      y: 35,
      width: 100,
      height: 38,
      text: "BACK",
      color: heroColors.neon,
      fontSize: "8px",
      onClick: () => this.goBack(),
    })
    this.backBtn.setDepth(110)

    // Add pointer events to sync selectedIndex/selectedButtonIndex
    this.inputManager.bindPointer(this.deployBtn, {
      pointerover: () => {
        if (!this.isTransitioning) {
          this.isButtonMode = true
          this.selectedButtonIndex = 0
          this.updateSelection(true)
        }
      },
    })

    this.inputManager.bindPointer(this.backBtn, {
      pointerover: () => {
        if (!this.isTransitioning) {
          this.isButtonMode = true
          this.selectedButtonIndex = 1
          this.updateSelection(true)
        }
      },
    })

    for (const [index, card] of this.cards.entries()) {
      const zone = card.list.find(
        (obj) => obj instanceof Phaser.GameObjects.Zone,
      ) as Phaser.GameObjects.Zone
      if (zone) {
        this.inputManager.bindPointer(zone, {
          pointerover: () => {
            if (!this.isTransitioning) {
              this.isButtonMode = false
              this.selectedIndex = index
              this.updateSelection(true)
            }
          },
        })
      }
    }

    this.add.existing(this.titleText)
    this.add.existing(this.deployBtn)
    this.add.existing(this.backBtn)
  }

  private navigate(dir: number): void {
    if (!this.isButtonMode) {
      const next =
        (this.selectedIndex + dir + CHARACTERS.length) % CHARACTERS.length
      if (next !== this.selectedIndex) {
        playClick(this)
        this.selectedIndex = next
        this.updateSelection(true)
      }
    } else {
      const next = (this.selectedButtonIndex + dir + 2) % 2
      if (next !== this.selectedButtonIndex) {
        playClick(this)
        this.selectedButtonIndex = next
        this.updateSelection(true)
      }
    }
  }

  private deploySelected(): void {
    if (this.isTransitioning) return
    this.isTransitioning = true
    playClick(this)

    const gs = GameState.instance
    gs.reset()
    gs.character = this.selected
    gs.playerName = this.selected.name.toUpperCase()
    gs.playerColor = this.selected.glowColor

    this.cameras.main.fadeOut(250, 0, 0, 0)
    this.time.delayedCall(270, () => {
      if (!PersistedData.isTutorialSeen()) {
        this.scene.start(SCENE.TUTORIAL, {
          nextScene: SCENE.WAVE,
          buttonText: "START WAVE",
        })
      } else {
        this.scene.start(SCENE.WAVE)
      }
    })
  }

  private goBack(): void {
    playClick(this)
    this.scene.start(SCENE.MENU)
  }
}
